import { Check, ExternalLink, CalendarCheck } from "lucide-react";
import { SEO } from "@/components/site/SEO";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

const VIDEO_ID = "_XYxtR03hLU";
const SHEET_COPY_URL = "https://docs.google.com/spreadsheets/d/1QHJko7l1qF2aWzIuP654Ib7osz8Ct89ImKOLoihZzuY/copy";
const BOOKING_URL = "https://calendly.com/shawnksfg/new-meeting";

const Step = ({ n, title, children }) => (
  <div className="flex gap-4">
    <span className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-full bg-gold/20 text-bronze font-serif text-lg">{n}</span>
    <div className="flex-1">
      <h2 className="font-serif text-xl text-legacy leading-tight">{title}</h2>
      <div className="mt-2 text-slate text-[0.95rem] leading-relaxed">{children}</div>
    </div>
  </div>
);

export default function BudgetTemplateAccess() {
  return (
    <div className="min-h-screen bg-ivory" data-testid="budget-template-access-page">
      <SEO title="Budget Template + Training \u2014 Access | Legacy Life & Retirement" noindex />
      <Header />
      <main className="relative bg-ivory pt-[120px] md:pt-[150px] pb-24 md:pb-28 overflow-hidden">
        <div className="pointer-events-none absolute inset-0 grid-lines opacity-40" />
        <div className="container-lr relative max-w-3xl">
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-gold text-legacy"><Check size={26} /></span>
          <p className="eyebrow mt-6">Request Confirmed</p>
          <h1 className="mt-3 font-serif font-semibold tracking-display text-legacy text-3xl md:text-4xl leading-[1.08]">
            Your Budget Template and Training Are Ready
          </h1>
          <p className="mt-5 text-slate text-base md:text-lg leading-relaxed">
            Watch the short training first, then open your own editable copy of the monthly budget template and begin organizing your income, expenses, savings, and debt.
          </p>

          <div className="mt-10 space-y-10">
            {/* Section 1 */}
            <Step n="1" title="Watch the Training Video">
              <div className="mt-4 aspect-video w-full rounded-card overflow-hidden bg-black border border-taupe/40">
                <iframe className="h-full w-full" src={`https://www.youtube-nocookie.com/embed/${VIDEO_ID}?rel=0`}
                  title="Budget Template Training" loading="lazy" allowFullScreen
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  data-testid="budget-access-video" />
              </div>
            </Step>

            {/* Section 2 */}
            <Step n="2" title="Open Your Monthly Budget Template">
              This button creates your own editable copy in Google Sheets. Your copy is private and will not change the original template.
              <div className="mt-4">
                <a href={SHEET_COPY_URL} target="_blank" rel="noopener noreferrer"
                  data-testid="budget-access-open-sheet" className="btn-base btn-gold text-[0.82rem] tracking-[0.1em] uppercase">
                  <ExternalLink size={16} />Open Your Budget Template
                </a>
              </div>
            </Step>

            {/* Section 3 */}
            <Step n="3" title="Need Help Building Your Financial System?">
              Book a free strategy call to see how your budget, cash flow, family banking system, and long-term financial strategy can work together.
              <div className="mt-4">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer"
                  data-testid="budget-access-book-call" className="btn-base btn-primary text-[0.82rem] tracking-[0.1em] uppercase">
                  <CalendarCheck size={16} />Book Your Free Strategy Call
                </a>
              </div>
            </Step>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
